/**
 * Meta match-key capture in the browser (Conversions API "parameter builder").
 *
 * Meta scores every event on Event Match Quality: the more identifiers an event
 * carries, the more conversions it can attribute. Three of the highest-value
 * keys only exist in the buyer's browser — `_fbp` (browser id), `_fbc` (the ad
 * click that brought them) and a stable `external_id` — so they have to be read
 * here and posted with the order, otherwise the server-side Purchase can never
 * send them.
 *
 * Two things this file does that the pixel alone does not:
 *   1. It captures the keys EVEN WHEN fbevents.js never loads (ad blocker, slow
 *      network, the deferred loader in lib/pixel.ts hasn't fired yet). We write
 *      the same cookies in Meta's own format, so a later fbevents.js adopts them
 *      instead of minting different ones.
 *   2. It persists `fbclid` at the moment of landing. The buyer usually lands on
 *      a sales page and checks out later; without this the click id is gone by
 *      the time an order is created.
 *
 * Cookie formats are Meta's and must not be "tidied": `fb.<subdomainIndex>.<
 * creationMs>.<payload>`, and the fbc payload is CASE-SENSITIVE.
 * Docs: https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
 */

const FBP_COOKIE = "_fbp";
const FBC_COOKIE = "_fbc";
const EXTERNAL_ID_KEY = "sellfy_xid";

/** Meta's own cookie lifetime for _fbp/_fbc. */
const COOKIE_MAX_AGE_S = 90 * 24 * 60 * 60;

/**
 * Meta's `subdomainIndex`: how many dots up from the right the cookie is set on.
 * fbevents.js writes on the registrable domain (`sellhiz.win`, `kedai.com.my`),
 * which is index 1 for a plain TLD. We write the cookie host-only, so we mirror
 * that value rather than compute one — a mismatched index would make our cookie
 * and a later fbevents.js cookie disagree about the same click.
 */
const SUBDOMAIN_INDEX = 1;

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(name: string, value: string): void {
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE_S}; Path=/; SameSite=Lax${secure}`;
  } catch {
    // no-op: cookies blocked just means this buyer carries fewer match keys
  }
}

/** Meta's fbp payload is a random 10-digit-ish number, not a uuid. */
function randomFbpPayload(): string {
  const buf = new Uint32Array(2);
  crypto.getRandomValues(buf);
  return `${buf[0]}${buf[1]}`.slice(0, 16);
}

/**
 * The browser id. Reuses the pixel's `_fbp` when fbevents.js already set one;
 * otherwise mints one in the same format so a blocked/slow pixel still yields a
 * usable key (and fbevents.js adopts ours when it does load).
 */
export function getFbp(): string | undefined {
  const existing = readCookie(FBP_COOKIE);
  if (existing) return existing;
  const minted = `fb.${SUBDOMAIN_INDEX}.${Date.now()}.${randomFbpPayload()}`;
  writeCookie(FBP_COOKIE, minted);
  return minted;
}

/**
 * The click id. `_fbc` only exists once someone arrives from an ad, so this
 * returns undefined for organic traffic — that is expected, not a failure.
 */
export function getFbc(): string | undefined {
  return readCookie(FBC_COOKIE) ?? undefined;
}

/**
 * Persist `?fbclid=` as `_fbc` on first sight. Call at app bootstrap: the click
 * lands on a sales page, and the query string is long gone by the time the buyer
 * reaches checkout. Last click wins (a fresh fbclid overwrites the stored one),
 * matching how fbevents.js itself behaves.
 */
export function captureFbclidFromUrl(): void {
  try {
    const fbclid = new URLSearchParams(window.location.search).get("fbclid");
    if (!fbclid) return;
    writeCookie(FBC_COOKIE, `fb.${SUBDOMAIN_INDEX}.${Date.now()}.${fbclid}`);
  } catch {
    // no-op
  }
}

/**
 * A stable pseudonymous id for this browser, sent raw to the pixel (which hashes
 * it) and hashed on the server for CAPI — both sides therefore derive the same
 * hash, which is what lets Meta stitch a buyer's browser events to the
 * server-side Purchase even when no email matched.
 */
export function getExternalId(): string | undefined {
  try {
    const existing = window.localStorage.getItem(EXTERNAL_ID_KEY);
    if (existing) return existing;
    const buf = new Uint8Array(16);
    crypto.getRandomValues(buf);
    const minted = [...buf].map((b) => b.toString(16).padStart(2, "0")).join("");
    window.localStorage.setItem(EXTERNAL_ID_KEY, minted);
    return minted;
  } catch {
    return undefined;
  }
}

/** Every browser-only match key, ready to post with an order. */
export interface FbBrowserParams {
  fbp?: string;
  fbc?: string;
  externalId?: string;
}

export function getFbBrowserParams(): FbBrowserParams {
  return { fbp: getFbp(), fbc: getFbc(), externalId: getExternalId() };
}
